import { useState, useEffect, useRef } from 'react';
import { 
  ChatCircle, 
  X, 
  PaperPlaneTilt,
  Calendar,
  CreditCard,
  IdentificationCard,
  FileText,
  CheckCircle,
  CaretRight,
  User,
  ArrowRight,
  Sparkle
} from '@phosphor-icons/react';

// Pre-defined booking assistant responses - no AI, just guided flow
const BOOKING_STEPS = {
  welcome: {
    message: "Hi! I'm your booking assistant. I'll help you schedule an appointment step by step. Would you like to start booking an appointment?",
    options: [
      { label: "Yes, let's start", next: "step_info" },
      { label: "I have a question", next: "faq" },
      { label: "No thanks", next: "goodbye" }
    ]
  },
  step_info: {
    message: "Great! First, we'll need to verify your personal information. This helps us connect your appointment to your medical records. Click the button below to begin.",
    highlight: 1, // Highlight step 1
    options: [
      { label: "Continue to Patient Info", action: "goto_step", step: 1 },
      { label: "What info do I need?", next: "info_details" }
    ]
  },
  info_details: {
    message: "You'll need to provide or verify:\n• Your full legal name\n• Date of birth\n• Phone number\n• Email address\n\nThis should match your government ID for accurate record matching.",
    options: [
      { label: "Got it, let's continue", action: "goto_step", step: 1 },
      { label: "Back to start", next: "welcome" }
    ]
  },
  step_schedule: {
    message: "Now let's pick your appointment date and time. Choose a date on the calendar, then select an available time slot. Grayed-out slots are already booked.",
    highlight: 2,
    options: [
      { label: "View Available Times", action: "goto_step", step: 2 },
      { label: "How far in advance can I book?", next: "schedule_info" }
    ]
  },
  schedule_info: {
    message: "You can book appointments up to 30 days in advance. Appointments are available Monday-Friday, 9AM-4PM (excluding 12PM-1PM lunch break). Each time slot shows remaining capacity.",
    options: [
      { label: "Select My Time", action: "goto_step", step: 2 },
      { label: "Back to start", next: "welcome" }
    ]
  },
  step_insurance: {
    message: "Next, we need your insurance card. Please upload photos of both the front and back. Even if we have your insurance on file, you can update it here.",
    highlight: 3,
    options: [
      { label: "Upload Insurance Card", action: "goto_step", step: 3 },
      { label: "I don't have insurance", next: "no_insurance" },
      { label: "Why is this needed?", next: "insurance_why" }
    ]
  },
  no_insurance: {
    message: "No problem! You can still book an appointment. Self-pay options are available. You can skip the insurance upload step or discuss payment options with our front desk.",
    options: [
      { label: "Continue without insurance", action: "goto_step", step: 3 },
      { label: "Back to start", next: "welcome" }
    ]
  },
  insurance_why: {
    message: "Your insurance card helps us:\n• Verify your coverage before your visit\n• Submit claims accurately\n• Estimate your out-of-pocket costs\n\nWe keep all documents secure and HIPAA-compliant.",
    options: [
      { label: "Upload My Card", action: "goto_step", step: 3 },
      { label: "Back", next: "step_insurance" }
    ]
  },
  step_id: {
    message: "For identity verification, please upload your driver's license, passport, or government-issued ID (front and back).",
    highlight: 4,
    options: [
      { label: "Upload ID", action: "goto_step", step: 4 },
      { label: "Why do you need my ID?", next: "id_why" }
    ]
  },
  id_why: {
    message: "Your ID helps us:\n• Verify your identity for medical record accuracy\n• Comply with healthcare regulations\n• Protect against medical identity theft\n\nYour documents are stored securely and only accessible to authorized staff.",
    options: [
      { label: "Upload My ID", action: "goto_step", step: 4 },
      { label: "Back", next: "step_id" }
    ]
  },
  step_consents: {
    message: "Almost done! Before your appointment, we need you to review and sign our consent forms. This includes HIPAA privacy consent and treatment authorization.",
    highlight: 5,
    options: [
      { label: "Review & Sign Consents", action: "goto_step", step: 5 },
      { label: "What am I signing?", next: "consent_info" }
    ]
  },
  consent_info: {
    message: "You'll be signing:\n• HIPAA Privacy Notice - how we protect your health information\n• Consent to Treatment - authorization for medical care\n• Financial Responsibility - understanding of billing practices\n\nYou can read each document before signing.",
    options: [
      { label: "Proceed to Consents", action: "goto_step", step: 5 },
      { label: "Back", next: "step_consents" }
    ]
  },
  step_confirm: {
    message: "You're all set! Review your appointment details and click 'Confirm Booking' to finalize. You'll receive a confirmation email shortly after.",
    highlight: 6,
    options: [
      { label: "Review & Confirm", action: "goto_step", step: 6 },
      { label: "I need to make changes", next: "changes" }
    ]
  },
  changes: {
    message: "No problem! Use the 'Back' buttons on each step to go back and make changes. Your information is saved as you go, so you won't lose progress.",
    options: [
      { label: "Go to Confirmation", action: "goto_step", step: 6 },
      { label: "Start Over", next: "welcome" }
    ]
  },
  faq: {
    message: "I'm happy to help! What would you like to know?",
    options: [
      { label: "What documents do I need?", next: "faq_docs" },
      { label: "How long does booking take?", next: "faq_time" },
      { label: "Can I reschedule later?", next: "faq_reschedule" },
      { label: "Start booking", next: "step_info" }
    ]
  },
  faq_docs: {
    message: "For a complete booking, please have ready:\n• Government-issued photo ID (driver's license, passport)\n• Insurance card (if applicable)\n• List of current medications (optional but helpful)",
    options: [
      { label: "Another question", next: "faq" },
      { label: "Start booking", next: "step_info" }
    ]
  },
  faq_time: {
    message: "The booking process typically takes 5-10 minutes if you have all documents ready. You can also save your progress and return later - your information will be saved.",
    options: [
      { label: "Another question", next: "faq" },
      { label: "Start booking", next: "step_info" }
    ]
  },
  faq_reschedule: {
    message: "Yes! After booking, you can request to reschedule or cancel from your Appointments page. Requests go to our staff for review, and you'll keep your original time until approved.",
    options: [
      { label: "Another question", next: "faq" },
      { label: "Start booking", next: "step_info" }
    ]
  },
  goodbye: {
    message: "No problem! I'm here whenever you're ready to book. Just click the chat icon to start a conversation. Have a great day!",
    options: [
      { label: "Actually, let's book now", next: "step_info" }
    ]
  }
};

// Map booking steps to their guide keys
const STEP_TO_GUIDE = {
  1: 'step_info',
  2: 'step_schedule', 
  3: 'step_insurance',
  4: 'step_id',
  5: 'step_consents',
  6: 'step_confirm'
};

export default function BookingHelper({ isOpen, onClose, currentStep, onGoToStep, isBookingOpen }) {
  const [messages, setMessages] = useState([]);
  const [currentGuide, setCurrentGuide] = useState('welcome');
  const [minimized, setMinimized] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize with welcome message
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      addBotMessage('welcome');
    }
  }, [isOpen]);

  // Update guide based on current booking step
  useEffect(() => {
    if (isBookingOpen && currentStep && STEP_TO_GUIDE[currentStep]) {
      const guideKey = STEP_TO_GUIDE[currentStep];
      if (guideKey !== currentGuide) {
        setCurrentGuide(guideKey);
        // Add contextual message for current step
        const guide = BOOKING_STEPS[guideKey];
        if (guide) {
          setMessages(prev => [...prev, {
            type: 'bot',
            text: `Step ${currentStep}: ${guide.message}`,
            options: guide.options,
            highlight: guide.highlight
          }]);
        }
      }
    }
  }, [currentStep, isBookingOpen]);

  const addBotMessage = (key) => {
    const guide = BOOKING_STEPS[key];
    if (guide) {
      setCurrentGuide(key);
      setMessages(prev => [...prev, {
        type: 'bot',
        text: guide.message,
        options: guide.options,
        highlight: guide.highlight
      }]);
    }
  };

  const handleOptionClick = (option) => {
    // Add user selection as message
    setMessages(prev => [...prev, {
      type: 'user',
      text: option.label
    }]);

    // Handle action
    if (option.action === 'goto_step' && option.step) {
      onGoToStep(option.step);
      // Don't add another message - the useEffect will handle step change
    } else if (option.next) {
      addBotMessage(option.next);
    }
  };

  const getStepIcon = (step) => {
    switch(step) {
      case 1: return User;
      case 2: return Calendar;
      case 3: return CreditCard;
      case 4: return IdentificationCard;
      case 5: return FileText;
      case 6: return CheckCircle;
      default: return Calendar;
    }
  };

  if (!isOpen) return null;

  if (minimized) {
    return (
      <button
        onClick={() => setMinimized(false)}
        className="fixed bottom-6 right-6 w-14 h-14 bg-teal-600 hover:bg-teal-700 text-white rounded-full shadow-lg flex items-center justify-center z-50 transition-all"
        data-testid="booking-helper-minimized"
      >
        <ChatCircle size={24} weight="fill" />
        <span className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center text-[10px] font-bold">
          ?
        </span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 w-80 md:w-96 bg-white rounded-2xl shadow-2xl z-50 flex flex-col max-h-[500px] border border-slate-200" data-testid="booking-helper">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-500 text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <Sparkle size={18} weight="fill" />
          </div>
          <div>
            <h3 className="font-semibold text-sm">Booking Assistant</h3>
            <p className="text-teal-100 text-xs">Here to help you schedule</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setMinimized(true)}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Minimize"
          >
            <span className="text-lg leading-none">−</span>
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            title="Close"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      {/* Step indicator (when booking is open) */}
      {isBookingOpen && currentStep && (
        <div className="px-4 py-2 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
          {[1, 2, 3, 4, 5, 6].map((step) => {
            const Icon = getStepIcon(step);
            return (
              <div
                key={step}
                className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${
                  step === currentStep
                    ? 'bg-teal-500 text-white'
                    : step < currentStep
                    ? 'bg-teal-100 text-teal-600'
                    : 'bg-slate-200 text-slate-400'
                }`}
              >
                <Icon size={14} />
              </div>
            );
          })}
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-[200px]">
        {messages.map((msg, index) => (
          <div key={index} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[85%] ${msg.type === 'user' ? 'order-2' : 'order-1'}`}>
              {msg.type === 'bot' && (
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkle size={14} className="text-teal-600" />
                  </div>
                  <div>
                    <div className="bg-slate-100 rounded-2xl rounded-tl-md px-4 py-2.5 text-slate-700 text-sm whitespace-pre-line">
                      {msg.text}
                    </div>
                    
                    {/* Options */}
                    {msg.options && index === messages.length - 1 && (
                      <div className="mt-2 space-y-1.5">
                        {msg.options.map((option, optIndex) => (
                          <button
                            key={optIndex}
                            onClick={() => handleOptionClick(option)}
                            className="w-full text-left px-3 py-2 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 hover:bg-teal-50 hover:border-teal-200 hover:text-teal-700 transition-colors flex items-center justify-between group"
                            data-testid={`helper-option-${optIndex}`}
                          >
                            {option.label}
                            <CaretRight size={14} className="text-slate-400 group-hover:text-teal-500" />
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {msg.type === 'user' && (
                <div className="bg-teal-500 text-white rounded-2xl rounded-tr-md px-4 py-2.5 text-sm">
                  {msg.text}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Quick actions footer */}
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
        <div className="flex gap-2 overflow-x-auto">
          <button
            onClick={() => addBotMessage('welcome')}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs text-slate-600 hover:bg-slate-100 whitespace-nowrap"
          >
            Start Over
          </button>
          <button
            onClick={() => addBotMessage('faq')}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-full text-xs text-slate-600 hover:bg-slate-100 whitespace-nowrap"
          >
            FAQ
          </button>
          {!isBookingOpen && (
            <button
              onClick={() => {
                addBotMessage('step_info');
                onGoToStep(1);
              }}
              className="px-3 py-1.5 bg-teal-500 text-white rounded-full text-xs hover:bg-teal-600 whitespace-nowrap flex items-center gap-1"
            >
              Book Now <ArrowRight size={12} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
